import type { OrderPayloadPricing } from '@/lib/types/orderPayload';

const SUBSTITUTION_BUFFER_RATE = 0.15;

export interface CalculatedOrderTotals {
  /** Order total excluding the substitution buffer. */
  netTotal: number;
  /** Amount temporarily authorized (includes the 15% capture buffer when shown). */
  authTotal: number;
  /** Buffer amount in dollars. */
  bufferAmount: number;
}

/**
 * Derives checkout display totals from Sylius pricing.
 * When the capture buffer is shown but not on the order yet, estimates the 15% buffer from the net total.
 */
export function calculateOrderTotals(
  pricing: OrderPayloadPricing | undefined,
  includeAuthorizationBuffer: boolean,
): CalculatedOrderTotals {
  const bufferOnOrder = Number(pricing?.buffer ?? 0);
  const orderTotal = Number(pricing?.total ?? 0);

  if (!includeAuthorizationBuffer) {
    const netTotal = orderTotal - bufferOnOrder;
    return {
      netTotal,
      authTotal: netTotal,
      bufferAmount: 0,
    };
  }

  if (bufferOnOrder > 0) {
    return {
      netTotal: orderTotal - bufferOnOrder,
      authTotal: orderTotal,
      bufferAmount: bufferOnOrder,
    };
  }

  const netTotal = orderTotal;
  const bufferAmount = Math.round(netTotal * SUBSTITUTION_BUFFER_RATE * 100) / 100;
  const authTotal = Math.round((netTotal + bufferAmount) * 100) / 100;

  return {
    netTotal,
    authTotal,
    bufferAmount,
  };
}
